/**
 * PitchBook Admin API
 * PitchBook Admin OpenAPI specification
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import {createAction} from '@reduxjs/toolkit';
import {ApiMethods, ConcurrentStrategies} from '@pb/middleware-api';
import {PayloadData} from '../types';
import type {Error} from '../models/Error';
import type {McpContract} from '../models/McpContract';
import type {McpCreateContractRequest} from '../models/McpCreateContractRequest';
import type {McpUpdateContractRequest} from '../models/McpUpdateContractRequest';
import type {McpUpdateCreditsRequest} from '../models/McpUpdateCreditsRequest';

export interface CreateMcpContract$Request {
    accountId: number;
    mcpCreateContractRequest: McpCreateContractRequest;
}

export interface CreateMcpContract$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const createMcpContractRequest = createAction<PayloadData<void, CreateMcpContract$Request>>('MCP_CONTRACTS__CREATE_MCP_CONTRACT__REQUEST');
export const createMcpContractSuccess = createAction<PayloadData<void, CreateMcpContract$Request>>('MCP_CONTRACTS__CREATE_MCP_CONTRACT__SUCCESS');
export const createMcpContractFailure = createAction<PayloadData<Error, CreateMcpContract$Request, true>>('MCP_CONTRACTS__CREATE_MCP_CONTRACT__FAILURE');

export const createMcpContractActions = [
    createMcpContractRequest,
    createMcpContractSuccess,
    createMcpContractFailure
] as const;

/**
 * Create a new MCP contract for an account
 * @param {number} meta.accountId Id of account
 * @param {McpCreateContractRequest} meta.mcpCreateContractRequest
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const createMcpContract = (meta: CreateMcpContract$Request, config?: CreateMcpContract$Config) => {
    return {
        CALL_API: {
            types: createMcpContractActions,
            method: ApiMethods.POST,
            endpoint: `/web-api/accounts/${meta.accountId}/mcp/contracts`,
            TRANSPORT_REQUEST_BODY: meta.mcpCreateContractRequest,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface GetMcpContracts$Request {
    accountId: number;
}

export interface GetMcpContracts$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getMcpContractsRequest = createAction<PayloadData<void, GetMcpContracts$Request>>('MCP_CONTRACTS__GET_MCP_CONTRACTS__REQUEST');
export const getMcpContractsSuccess = createAction<PayloadData<Array<McpContract>, GetMcpContracts$Request>>('MCP_CONTRACTS__GET_MCP_CONTRACTS__SUCCESS');
export const getMcpContractsFailure = createAction<PayloadData<Error, GetMcpContracts$Request, true>>('MCP_CONTRACTS__GET_MCP_CONTRACTS__FAILURE');

export const getMcpContractsActions = [
    getMcpContractsRequest,
    getMcpContractsSuccess,
    getMcpContractsFailure
] as const;

/**
 * Get all MCP contracts for an account
 * @param {number} meta.accountId Id of account
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const getMcpContracts = (meta: GetMcpContracts$Request, config?: GetMcpContracts$Config) => {
    return {
        CALL_API: {
            types: getMcpContractsActions,
            method: ApiMethods.GET,
            endpoint: `/web-api/accounts/${meta.accountId}/mcp/contracts`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface UpdateMcpContract$Request {
    contractId: number;
    mcpUpdateContractRequest: McpUpdateContractRequest;
}

export interface UpdateMcpContract$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const updateMcpContractRequest = createAction<PayloadData<void, UpdateMcpContract$Request>>('MCP_CONTRACTS__UPDATE_MCP_CONTRACT__REQUEST');
export const updateMcpContractSuccess = createAction<PayloadData<void, UpdateMcpContract$Request>>('MCP_CONTRACTS__UPDATE_MCP_CONTRACT__SUCCESS');
export const updateMcpContractFailure = createAction<PayloadData<Error, UpdateMcpContract$Request, true>>('MCP_CONTRACTS__UPDATE_MCP_CONTRACT__FAILURE');

export const updateMcpContractActions = [
    updateMcpContractRequest,
    updateMcpContractSuccess,
    updateMcpContractFailure
] as const;

/**
 * Update an existing MCP contract
 * @param {number} meta.contractId Id of MCP contract
 * @param {McpUpdateContractRequest} meta.mcpUpdateContractRequest
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const updateMcpContract = (meta: UpdateMcpContract$Request, config?: UpdateMcpContract$Config) => {
    return {
        CALL_API: {
            types: updateMcpContractActions,
            method: ApiMethods.PUT,
            endpoint: `/web-api/mcp/contracts/${meta.contractId}`,
            TRANSPORT_REQUEST_BODY: meta.mcpUpdateContractRequest,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

export interface UpdateMcpContractCredits$Request {
    contractId: number;
    mcpUpdateCreditsRequest: McpUpdateCreditsRequest;
}

export interface UpdateMcpContractCredits$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const updateMcpContractCreditsRequest = createAction<PayloadData<void, UpdateMcpContractCredits$Request>>('MCP_CONTRACTS__UPDATE_MCP_CONTRACT_CREDITS__REQUEST');
export const updateMcpContractCreditsSuccess = createAction<PayloadData<void, UpdateMcpContractCredits$Request>>('MCP_CONTRACTS__UPDATE_MCP_CONTRACT_CREDITS__SUCCESS');
export const updateMcpContractCreditsFailure = createAction<PayloadData<Error, UpdateMcpContractCredits$Request, true>>('MCP_CONTRACTS__UPDATE_MCP_CONTRACT_CREDITS__FAILURE');

export const updateMcpContractCreditsActions = [
    updateMcpContractCreditsRequest,
    updateMcpContractCreditsSuccess,
    updateMcpContractCreditsFailure
] as const;

/**
 * Update credits for an MCP contract
 * @param {number} meta.contractId Id of MCP contract
 * @param {McpUpdateCreditsRequest} meta.mcpUpdateCreditsRequest
 * @param {boolean} [config.bulk]
 * @param {typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies]} [config.concurrentStrategy]
 * @param {AbortSignal} [config.signal]
 */
export const updateMcpContractCredits = (meta: UpdateMcpContractCredits$Request, config?: UpdateMcpContractCredits$Config) => {
    return {
        CALL_API: {
            types: updateMcpContractCreditsActions,
            method: ApiMethods.PUT,
            endpoint: `/web-api/mcp/contracts/${meta.contractId}/credits`,
            TRANSPORT_REQUEST_BODY: meta.mcpUpdateCreditsRequest,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            }
        },
        ...meta
    }
};

